import { useState, useEffect, useMemo } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useSelector } from 'react-redux';

import CardLoader from 'wrapper/card-loader';
import {
  company_logo,
  roles
} from 'constants/constant';
import contentStyles from './app-content.module.css';

// Services
import authService from 'services/auth/authService';
import {
  getCurrentOrgSlug,
  getTenantScopedStorageKey,
  getCurrentStateId,
  persistStateId,
  stripStatePrefix,
  getLicensedStatesFromCache,
} from 'utils/tenant';
import useApplyContext from 'hooks/useApplyContext';
import { landingPathForRole } from 'utils/roles';

// Redux
import { 
  selectIsAuthenticated, 
  selectIsLoading, 
  selectUser,
} from 'redux/slices/authSlice';
import { userRoleData } from 'redux/slices/userRole';

// Routes
import consumerRoutes from 'routes/pages/consumer';
import officerRoutes from 'routes/pages/loan-officer';
import { authRoutes } from 'routes/pages/auth';
import organizationRoutes from 'routes/pages/organization';
import leadGenRoutes from 'routes/pages/lead-gen';

// Components
import ConsumerLogin from '@/pages/shared/auth/login/ConsumerLogin';
import RegisterForm from '../auth/RegisterForm';

const normalizeRouteDefinitions = (routes = []) =>
  routes.map((route) => {
    const normalized = { ...route };
    if (typeof normalized.path === 'string') {
      if (normalized.path === '*') {
        // keep wildcard path as-is
      } else if (normalized.path.startsWith('/')) {
        const trimmed = normalized.path.replace(/^\/+/, '');
        normalized.path = trimmed;
      }

      if (normalized.path === '') {
        delete normalized.path;
      }
    }

    if (normalized.children) {
      normalized.children = normalizeRouteDefinitions(normalized.children);
    }

    return normalized;
  });

// All routes mount at root, with no state prefix. Legacy /XX/... links fall
// through to each tree's `*` route, whose NotFound redirects them to the path
// without the prefix (components/404). A top-level `/:stateId/*` route used to
// do that here, but it outranked every tree's `*` and rendered nothing for an
// unknown path, so a mistyped URL was a blank page.
export const wrapRoutesWithState = (routes) => normalizeRouteDefinitions(routes);

const TenantStateSelector = ({ states }) => {
  const currentState = getCurrentStateId();
  if (!states || states.length <= 1) {
    return null;
  }

  const handleStateChange = (event) => {
    const nextState = event.target.value;
    if (!nextState || nextState === currentState) return;
    persistStateId(nextState);
    // State is stored in sessionStorage only — no URL change needed
    window.location.reload();
  };

  return (
    <div className="absolute top-4 right-4 z-40 flex items-center gap-2 rounded-full border border-primary200 bg-white/90 px-4 py-1 shadow">
      <span className="text-xs font-semibold uppercase tracking-wide text-primary700">State</span>
      <select
        value={currentState || ''}
        onChange={handleStateChange}
        className="bg-transparent text-sm text-primary900 focus:outline-none"
      >
        {states.map((state) => (
          <option key={state} value={state}>
            {state}
          </option>
        ))}
      </select>
    </div>
  );
};

const AppRouter = ({ isRoute, routes, routerKey = 'default', stateOptions = [] }) => {
  const router = useMemo(() => {
    if (routes && routes.length > 0) {
      return createBrowserRouter(wrapRoutesWithState(routes, stateOptions));
    }
    return null;
  }, [routes, stateOptions]);

  if (!isRoute || !router) return null;

  return <RouterProvider key={routerKey} router={router} />;
};

const AppContentNew = () => {
  const { toast } = useToast();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const sliceRole = useSelector(userRoleData);
  const effectiveUserRole = useMemo(() => {
    if (user?.role) {
      return user.role;
    }
    return sliceRole?.userRole || '';
  }, [user?.role, sliceRole?.userRole]);

  const role = useMemo(() => ({ userRole: effectiveUserRole }), [effectiveUserRole]);
  
  const locationSearch = window.location.search;
  const searchParams = new URLSearchParams(locationSearch);
  const referralCode = searchParams.get('ref');
  const currentPathname = stripStatePrefix(window.location.pathname);
  const isApplyRoute = currentPathname.startsWith('/apply');
  const isAdminRoute = currentPathname.startsWith('/admin');
  const isPlatformRoute = currentPathname.startsWith('/platform');
  const isOnboardRoute = currentPathname.startsWith('/onboard');
  const STATELESS_AUTH_PATHS = [
    '/result_page',
    '/session-timeout',
    '/login',
    '/login/b2b',
    '/login/consumer',
    '/account-selection',
  ];
  const isAuthRoute = currentPathname.startsWith('/auth/') ||
    currentPathname.startsWith('/branch/auth/') ||
    STATELESS_AUTH_PATHS.includes(currentPathname);
  // Old links to retired auth screens never reach here: main.jsx rewrites them
  // to /login before the app renders (utils/retiredAuthPaths).
  const isLoginPath = ['/', '/login', '/login/b2b', '/login/consumer', '/account-selection'].includes(currentPathname);

  // Check if we need to show router immediately for certain routes
  const needsImmediateRouter = isOnboardRoute || isAuthRoute;
  
  const [isInitializing, setIsInitializing] = useState(!needsImmediateRouter);
  const [showConsumerAuth, setShowConsumerAuth] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [routerVersion, setRouterVersion] = useState(0);

  const orgSlug = getCurrentOrgSlug();
  const userSelectionStorageKey = getTenantScopedStorageKey('userSelection', orgSlug);
  const [licensedStates, setLicensedStates] = useState([]);

  useEffect(() => {
    const loadStates = () => {
      const states = getLicensedStatesFromCache(orgSlug);
      if (states.length) {
        setLicensedStates(states);
      }
    };
    loadStates();
    if (typeof window === 'undefined') {
      return () => {};
    }
    const handler = (event) => {
      if (event.key && event.key.includes(':organization')) {
        loadStates();
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [orgSlug]);

  const {
    context: applyContext,
    status: applyStatus,
    error: applyError,
    load: loadApplyContext,
    clear: clearApplyContext,
  } = useApplyContext({ autoLoad: false });
  
  // Determine flow type based on entry point
  const flowType = useMemo(() => {
    if (referralCode || isApplyRoute) return 'consumer';
    if (isAdminRoute) return 'b2b';
    if (isPlatformRoute) return 'platform';
    return null;
  }, [referralCode, isApplyRoute, isAdminRoute, isPlatformRoute]);

  useEffect(() => {
    console.log('[AppContentNew] Auth state:', {
      isAuthenticated,
      role: role?.userRole,
      pathname: currentPathname
    });
  }, [isAuthenticated, role, currentPathname]);

  // Redirect authenticated users away from login pages to their landing page.
  // Without this, the role-based router renders at a login URL, which no role
  // tree defines, so the user lands on Page not found instead of their home.
  useEffect(() => {
    if (isAuthenticated && role?.userRole && isLoginPath) {
      const rawRole = user?.originalRole || user?.role || role?.userRole;
      const targetPath = landingPathForRole(rawRole);
      if (targetPath && targetPath !== currentPathname) {
        window.history.replaceState(null, '', targetPath);
        setRouterVersion(v => v + 1);
      }
    }
  }, [isAuthenticated, role?.userRole, isLoginPath, currentPathname]);

  useEffect(() => {
    const params = new URLSearchParams(locationSearch);
    if (params.get('redirected') === '1') {
      toast.info('We redirected you to the right branch portal.');
    }
  }, [locationSearch, toast]);

  useEffect(() => {
    if (flowType === 'consumer' && applyStatus === 'idle') {
      loadApplyContext();
    }
  }, [flowType, applyStatus, loadApplyContext]);

  useEffect(() => {
    // Check for existing auth on mount
    const initAuth = async () => {
      console.log('[AppContentNew] Initializing auth');

      // Check for stored user role
      localStorage.getItem(userSelectionStorageKey);

      // If consumer flow, show auth directly
      if (flowType === 'consumer') {
        setShowConsumerAuth(true);
      }

      // If B2B flow and not already authenticated, redirect to login
      if (flowType === 'b2b' && !isAuthenticated) {
        console.log('[AppContentNew] B2B flow detected but user not authenticated, redirecting to login');
        authService.initiateB2BLogin();
      } else if (flowType === 'b2b' && isAuthenticated) {
        console.log('[AppContentNew] B2B flow detected and user already authenticated, skipping redirect');
      }

      setIsInitializing(false);
    };

    initAuth();
  }, [flowType, userSelectionStorageKey, isAuthenticated]);

  const handleLetsBegin = () => {
    window.location.assign('/login/b2b');
  };

  const getWidthAndHeight = () => {
    const paths = [
      '/result_page',
      '/session-timeout', '/auth/callback', '/branch/auth/callback', '/onboard',
      '/login',
      '/login/b2b', '/login/consumer', '/account-selection'
    ];
    const isPathMatch = paths.includes(currentPathname);
    if (role?.userRole || isPathMatch) return contentStyles.fullSize;
    return contentStyles.cardSize;
  };

  const getRoutes = () => {
    switch (role?.userRole) {
      case roles.consumer:
        return consumerRoutes;
      case roles.loanOfficer:
        return officerRoutes;
      case roles.leadGen:
        return leadGenRoutes;
      case "organization":
      case roles.branchAdmin:
      case roles.loanProcessor:
      case roles.superAdmin:
        return organizationRoutes;
      default:
        return authRoutes;
    }
  };

  const activeRoutes = role?.userRole ? getRoutes() : authRoutes;

  const routerKey = role?.userRole ? `role:${role.userRole}:v${routerVersion}` : `auth:v${routerVersion}`;

  // While redirecting from a login page, show a loader instead of the stale router
  if (isAuthenticated && role?.userRole && isLoginPath) return <CardLoader />;

  // Don't show loader for routes that need immediate rendering
  if (!needsImmediateRouter && (isLoading || isInitializing)) return <CardLoader />;

  // Determine if we should show the router
  const shouldShowRouter = role?.userRole || needsImmediateRouter;

  return (
    <div className={contentStyles.appRoot}>
      <div className={`${getWidthAndHeight()} tertiary-bg rounded-lg flex flex-col items-center`}>
        
        {/* Show routes if user has a role OR accessing auth routes */}
        {shouldShowRouter && (
          <AppRouter
            isRoute={true}
            routes={activeRoutes}
            routerKey={routerKey}
            stateOptions={licensedStates}
          />
        )}
        
        {/* Consumer Auth Flow (via referral or /apply) */}
        {showConsumerAuth && !role?.userRole && (
          <div className="w-full h-full flex flex-col items-center p-8">
            <div className="bg-secondary100 w-full flex justify-center rounded-t-lg -m-8 mb-4">
              <img 
                src={company_logo || '/Logo.png'} 
                alt="Company Logo" 
                className="h-24 py-1" 
              />
            </div>

            {applyStatus === 'loading' && (
              <div className="w-full mb-4 rounded-md bg-primary100 text-primary px-4 py-3 text-sm">
                Loading loan officer details...
              </div>
            )}

            {applyStatus === 'error' && applyError && (
              <div className="w-full mb-4 rounded-md bg-red-50 text-red-600 px-4 py-3 text-sm">
                <p>{applyError}</p>
                <div className="mt-2 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={loadApplyContext}
                    className="text-red-600 underline text-xs"
                  >
                    Try again
                  </button>
                  <a
                    href="/apply"
                    className="text-red-600 underline text-xs"
                    onClick={clearApplyContext}
                  >
                    Go to general application
                  </a>
                </div>
              </div>
            )}

            {applyStatus === 'ready' && applyContext?.loan_officer_name && (
              <div className="w-full mb-4 rounded-md bg-primary50 text-primary px-4 py-3 text-sm">
                <p className="font-semibold">
                  You&apos;re applying with {applyContext.loan_officer_name}
                  {applyContext.loan_officer_id ? ` (ID ${applyContext.loan_officer_id})` : ''}
                </p>
                <p className="text-primary/80 mt-1">
                  {applyContext.branch_slug ? `Branch: ${applyContext.branch_slug.replace(/-/g, ' ')}` : 'Organization portal'}
                  {applyContext.organization_slug ? ` • ${applyContext.organization_slug.toUpperCase()}` : ''}
                  {applyContext.loan_officer_email ? ` • ${applyContext.loan_officer_email}` : ''}
                </p>
              </div>
            )}
            
            {isRegistering ? (
              <RegisterForm referralCode={referralCode} />
            ) : (
              <>
                <ConsumerLogin />
                <div className="mt-4 text-center">
                  <span className="text-gray-600 text-sm">Don&apos;t have an account? </span>
                  <button
                    onClick={() => setIsRegistering(true)}
                    className="text-primary text-sm hover:underline"
                  >
                    Sign Up
                  </button>
                </div>
              </>
            )}
            
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setShowConsumerAuth(false);
                  setIsRegistering(false);
                  clearApplyContext();
                }}
                className="text-gray-500 text-sm hover:underline"
              >
                ← Back
              </button>
            </div>
          </div>
        )}
        
        {/* Default Welcome Screen (no referral, no specific route) */}
        {!shouldShowRouter && !flowType && !role?.userRole && !showConsumerAuth && (
          <>
            <img 
              src={company_logo || '/Logo.png'} 
              alt="Company Logo" 
              className="h-20 mt-8" 
            />
            <h3 className="h3-b text-secondary mt-4 mb-2">
              Welcome to Mira
            </h3>
            <p className="p-sm-regular text-secondary">
              Mira-culous loan services powered by AI.
            </p>
            <button
              className={`btn-primary p-sm-bold text-tertiary mt-12 flex flex-row gap-2 ${contentStyles.letsBeginButton}`}
              onClick={handleLetsBegin}
            >
              Let&apos;s Begin
            </button>
            
            {/* Platform Admin Link - only show on main domain, not organization subdomains */}
            {(() => {
              const hostname = window.location.hostname;
              const hasSubdomain = hostname.includes('.') && !hostname.startsWith('www.');
              const hasOrgParam = searchParams.get('org') || searchParams.get('queryorg');
              const isMainDomain = !hasSubdomain && !hasOrgParam;

              return isMainDomain && (
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <a
                    href="/platform/login"
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Platform Administration
                  </a>
                </div>
              );
            })()}
          </>
        )}
      </div>

      <TenantStateSelector states={licensedStates} />
      <Toaster />
    </div>
  );
};

export default AppContentNew;
